import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "What is a log collection agent?" (log shippers explained).
 * The educational/GEO framing of the Agent Fleet; /agents is the spoke.
 */
export function LogCollectionAgentGuideBody() {
  return (
    <>
      <Lead>
        Before logs can be searched or analysed, they have to get from the servers,
        containers and services that produce them to your log platform. That job
        belongs to a log collection agent, also called a log shipper. This guide
        explains what a log collection agent does, the main options (Vector, Fluent
        Bit, Fluentd, Filebeat, the OpenTelemetry Collector), and how to choose.
      </Lead>

      <GuideSection id="what-is-it" title="What is a log collection agent?">
        <P>
          A <Term>log collection agent</Term> (or <Term>log shipper</Term>) is a small
          program that runs on or near your systems, reads the logs they produce,
          optionally parses and transforms them, and forwards them to a central
          destination. It is the first stage of the log pipeline. Without it, logs
          stay scattered on individual machines where they are useless for
          investigation or monitoring.
        </P>
      </GuideSection>

      <GuideSection id="what-it-does" title="What a log collection agent does">
        <OL>
          <LI><Term>Collect</Term>: tail files, read container stdout, scrape system journals, or receive over a port.</LI>
          <LI><Term>Parse &amp; transform</Term>: structure raw lines, add metadata (host, environment), and optionally redact sensitive data.</LI>
          <LI><Term>Buffer</Term>: hold events on disk or in memory so nothing is lost if the destination is briefly unavailable.</LI>
          <LI><Term>Forward</Term>: ship the events to the log platform reliably, with retries and back-pressure.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="the-options" title="The main log shippers">
        <GuideTable
          head={['Agent', 'Built with', 'Best known for']}
          rows={[
            ['Vector', 'Rust', 'High-performance, modern pipeline for logs and metrics; rich transforms.'],
            ['Fluent Bit', 'C', 'Extremely lightweight and fast; the default in many Kubernetes distros.'],
            ['Fluentd', 'Ruby', 'Large plugin ecosystem; long-established, JSON-centric.'],
            ['Filebeat', 'Go', 'Lightweight file shipper from the Elastic Beats family.'],
            ['OpenTelemetry Collector', 'Go', 'Vendor-neutral, multi-signal (logs, metrics, traces), the universal telemetry pipeline.'],
          ]}
        />
        <P>
          There is no single winner: Fluent Bit excels at lightweight node-level
          collection, the OpenTelemetry Collector is the vendor-neutral choice for
          unified telemetry, and Vector is a high-performance pipeline with powerful
          in-flight transforms. Many estates even combine them (e.g. Fluent Bit at the
          node, a collector centrally).
        </P>
      </GuideSection>

      <GuideSection id="how-to-choose" title="How to choose">
        <UL>
          <LI><Term>Resource footprint</Term>: node and edge collection rewards lightweight agents.</LI>
          <LI><Term>Reliability under load</Term>: look at buffering, back-pressure, and delivery guarantees at your throughput.</LI>
          <LI><Term>Transforms</Term>: how much parsing, enrichment and redaction you need in-flight.</LI>
          <LI><Term>Open standards</Term>: OTLP support avoids lock-in and unifies logs, metrics and traces.</LI>
          <LI><Term>Operability</Term>: how easily you can deploy, configure and upgrade the fleet at scale.</LI>
        </UL>
        <Callout title="Collection is also a security boundary">
          <p>
            Agents run broadly across your estate and forward data out, so treat them
            as a security concern: authenticated transport, scoped credentials per
            agent, and central control over what each agent ships and where.
          </p>
        </Callout>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse handles log collection">
        <P>
          The LogPulse agent uses <Term>Vector</Term> as its runtime, wrapped in a
          control plane for fleet management: one-command install, server groups,
          per-agent authentication, and an EU-hosted control plane. It also accepts{' '}
          <Term>OTLP</Term> and a REST API, so you can ship with the OpenTelemetry
          Collector or any agent you already run. See{' '}
          <GuideLink href="/agents">Agent Fleet</GuideLink> for the full picture,{' '}
          <GuideLink href="/guides/structured-logging">structured logging</GuideLink>{' '}
          for getting clean data at the source, and{' '}
          <GuideLink href="/guides/what-is-log-management">what is log management</GuideLink>{' '}
          for where collection fits in the pipeline.
        </P>
      </GuideSection>
    </>
  );
}
